import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import LoginPage from './pages/LoginPage';
import Dashboard from './pages/Dashboard';
import FleetManagement from './pages/FleetManagement';
import ScheduleManagement from './pages/ScheduleManagement';
import WashHistory from './pages/WashHistory';
import Analytics from './pages/Analytics';
import Profile from './pages/Profile';
import Employees from './pages/Employees';
import Customers from './pages/Customers';
import { Toaster } from './components/ui/sonner';
import './App.css';

import { API, authFetch } from './utils/api';

function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash;
    const params = new URLSearchParams(hash.substring(1));
    const sessionId = params.get('session_id');

    if (!sessionId) {
      navigate('/login');
      return;
    }

    const processSession = async () => {
      try {
        const response = await fetch(`${API}/auth/google/session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_id: sessionId })
        });

        if (!response.ok) throw new Error('Session processing failed');

        const data = await response.json();
        if (data.session_token) {
          localStorage.setItem('session_token', data.session_token);
        }
        navigate('/dashboard', { replace: true });
      } catch (error) {
        console.error('Auth error:', error);
        navigate('/login');
      }
    };

    processSession();
  }, [navigate, location]);

  return <div className="min-h-screen flex items-center justify-center">Processing authentication...</div>;
}

function ProtectedRoute({ children, adminOnly = false }) {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await authFetch(`${API}/auth/me`);
        
        if (!response.ok) throw new Error('Not authenticated');
        
        const userData = await response.json();
        setUser(userData);
        setIsAuthenticated(true);
      } catch (error) {
        setIsAuthenticated(false);
        navigate('/login');
      }
    };

    checkAuth();
  }, [navigate, location]);

  if (isAuthenticated === null) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check admin-only routes
  const isAdmin = user?.role === 'administrator' || user?.role === 'admin';
  if (adminOnly && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return <div className="user-context">{typeof children === 'function' ? children({ user }) : children}</div>;
}

function AppRouter() {
  const location = useLocation();
  
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/dashboard" element={<ProtectedRoute>{({ user }) => <Dashboard user={user} />}</ProtectedRoute>} />
      <Route path="/fleet" element={<ProtectedRoute adminOnly>{({ user }) => <FleetManagement user={user} />}</ProtectedRoute>} />
      <Route path="/schedule" element={<ProtectedRoute>{({ user }) => <ScheduleManagement user={user} />}</ProtectedRoute>} />
      <Route path="/history" element={<ProtectedRoute>{({ user }) => <WashHistory user={user} />}</ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute adminOnly>{({ user }) => <Analytics user={user} />}</ProtectedRoute>} />
      <Route path="/employees" element={<ProtectedRoute adminOnly>{({ user }) => <Employees user={user} />}</ProtectedRoute>} />
      <Route path="/customers" element={<ProtectedRoute adminOnly>{({ user }) => <Customers user={user} />}</ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute>{({ user }) => <Profile user={user} />}</ProtectedRoute>} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

export default App;